import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Crown, Zap, Command as CommandIcon, Gift, Check, Minus, Loader2,
  CreditCard, Receipt, Building2, AlertTriangle, Sparkles, Info, X, ShieldCheck, RefreshCcw,
} from "lucide-react";
import { api } from "../lib/api";
import RazorpayCheckoutButton, { preloadRazorpaySDK } from "../components/RazorpayCheckoutButton";

const NAVY = "#1E3A5F";
const inr = (paise) => `₹${(Number(paise || 0) / 100).toLocaleString("en-IN")}`;
const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
};

const PLAN_META = {
  trial:    { icon: Gift,        mode: "trial",        accent: "#64748b", cadence: null,                 badge: null },
  monthly:  { icon: Zap,         mode: "subscription", accent: "#0ea5e9", cadence: "per month",          badge: null },
  yearly:   { icon: CommandIcon, mode: "subscription", accent: "#6366f1", cadence: "per year",           badge: "Most Popular" },
  lifetime: { icon: Crown,       mode: "order",        accent: "#b45309", cadence: "one-time, 1 year",   badge: "Best Value" },
};

const CARD_FEATURES = {
  trial:    ["Full C-ORE access", "Executive & Financial Intelligence", "No card required", "One trial per company"],
  monthly:  ["All executive modules", "Financial Sync & Command Center", "WhatsApp alerts", "Cancel anytime"],
  yearly:   ["Everything in Pulse", "Priority support", "Best annual value", "2 months effectively free"],
  lifetime: ["Everything, for 1 year", "Priority support", "Renew anytime after expiry", "One-time payment"],
};

// Client-side presentation labels only — no billing/API/pricing change.
const CTA_LABELS = { monthly: "Start Pulse", yearly: "Upgrade to Command", lifetime: "Get Sovereign (1 Year)" };

const TRUST_INDICATORS = [
  { icon: ShieldCheck, text: "Secure payments powered by Razorpay" },
  { icon: Receipt, text: "GST Invoice included" },
  { icon: Sparkles, text: "Instant activation" },
  { icon: RefreshCcw, text: "Cancel anytime" },
];

// Presentation matrix (admin-managed catalog is a deferred Phase-2 task).
const COMPARISON = [
  { label: "Executive Intelligence", trial: true, monthly: true, yearly: true, lifetime: true },
  { label: "Financial Intelligence", trial: true, monthly: true, yearly: true, lifetime: true },
  { label: "Command Center",         trial: true, monthly: true, yearly: true, lifetime: true },
  { label: "Financial Sync",         trial: true, monthly: true, yearly: true, lifetime: true },
  { label: "Advisors",               trial: true, monthly: true, yearly: true, lifetime: true },
  { label: "Fund Module",            trial: true, monthly: true, yearly: true, lifetime: true },
  { label: "WhatsApp Alerts",        trial: true, monthly: true, yearly: true, lifetime: true },
  { label: "Priority Support",       trial: false, monthly: false, yearly: true, lifetime: true },
  { label: "Future Updates",         trial: true, monthly: true, yearly: true, lifetime: true },
];

const STATUS_STYLES = {
  active:    { label: "Active",    cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  trialing:  { label: "Trial",     cls: "bg-sky-50 text-sky-700 border-sky-200" },
  cancelled: { label: "Cancelled", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  expired:   { label: "Expired",   cls: "bg-rose-50 text-rose-700 border-rose-200" },
  none:      { label: "No plan",   cls: "bg-slate-100 text-slate-600 border-slate-200" },
  lifetime:  { label: "Lifetime",  cls: "bg-amber-50 text-amber-800 border-amber-200" },
};

const Section = ({ title, subtitle, children, id, testid }) => (
  <section id={id} data-testid={testid} className="space-y-3">
    <div>
      <h2 className="text-[15px] font-bold" style={{ color: NAVY }}>{title}</h2>
      {subtitle && <p className="text-[12.5px] text-slate-500">{subtitle}</p>}
    </div>
    {children}
  </section>
);

const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse rounded-lg bg-slate-100 ${className}`} />
);

const PlaceholderCard = ({ icon: Icon, title, note, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-dashed border-slate-200 bg-white/60 p-5 flex items-start gap-3">
    <div className="rounded-lg bg-slate-50 p-2 text-slate-400"><Icon className="h-4 w-4" /></div>
    <div>
      <div className="text-[13px] font-semibold text-slate-700">{title}</div>
      <div className="text-[12px] text-slate-400 mt-0.5">{note}</div>
    </div>
  </div>
);

export default function PlansBilling() {
  const [plans, setPlans] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [ent, setEnt] = useState(null);
  const [history, setHistory] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [coupon, setCoupon] = useState("");
  const [couponMsg, setCouponMsg] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);

  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [selectedPlanCode, setSelectedPlanCode] = useState(null);

  // Warm the Razorpay SDK as soon as the Billing page mounts, so the modal
  // opens instantly once someone clicks a plan. Pure perf — zero change to
  // payment/checkout logic (same script, same instance, cached).
  useEffect(() => { preloadRazorpaySDK(); }, []);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("current_customer") || localStorage.getItem("current_customer");
      if (raw) setCustomer(JSON.parse(raw));
    } catch { /* ignore */ }
  }, []);

  const loadMe = useCallback(async () => {
    const me = await api.billingMe();
    if (me.ok) setEnt(me.data.entitlement);
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    const [p, me, h] = await Promise.all([api.billingPlans(), api.billingMe(), api.billingHistory()]);
    if (p.ok) { setPlans(p.data.plans || []); setCfg(p.data); }
    else setErr(p.error || "Could not load plans.");
    if (me.ok) setEnt(me.data.entitlement);
    if (h.ok) setHistory(h.data.payments || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const notify = (m) => { setToast(m); setTimeout(() => setToast(null), 3500); };

  const planName = useMemo(() => {
    const map = {}; plans.forEach((p) => { map[p.code] = p.name; });
    return (code) => map[code] || (code ? code : "No active plan");
  }, [plans]);

  const currentCode = ent?.lifetime ? "lifetime" : ent?.plan_code;

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    setCouponBusy(true); setCouponMsg(null);
    // Validate against Lifetime (coupons fully apply to one-time orders).
    const res = await api.billingValidateCoupon("lifetime", coupon.trim());
    const d = res.data || {};
    setCouponMsg(d.valid
      ? { ok: true, text: `Applied — you save ${inr(d.discount_paise)} on Sovereign (final ${inr(d.final_amount_paise)}).` }
      : { ok: false, text: `Invalid coupon: ${(d.reason || res.error || "not valid").replace(/_/g, " ")}` });
    setCouponBusy(false);
  };

  const doCancel = async () => {
    setCancelBusy(true);
    const res = await api.billingSubscriptionCancel();
    setCancelBusy(false); setConfirmCancel(false);
    if (res.ok) { notify("Auto-renewal cancelled. You keep access until the paid period ends."); loadMe(); }
    else notify(`Could not cancel: ${res.data?.detail || res.error}`);
  };

  const onCheckoutResult = (r) => {
    const TERMINAL = ["verified", "verify_failed", "create_failed", "payment_failed", "error", "dismissed"];
    if (r.stage === "start") { setSelectedPlanCode(r.planCode || "trial"); return; }
    if (r.stage === "verified" && r.ok) { notify("Payment verified — your plan is now active."); load(); }
    else if (r.stage === "verify_failed" || r.stage === "create_failed" || r.stage === "payment_failed" || r.stage === "error")
      notify(r.error || "Checkout could not be completed.");
    if (TERMINAL.includes(r.stage)) setSelectedPlanCode(null);
  };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const status = ent?.lifetime ? STATUS_STYLES.lifetime : (STATUS_STYLES[ent?.billing_status] || STATUS_STYLES[ent?.entitlement_status] || STATUS_STYLES.none);
  const isRecurringActive = ent && ["monthly", "yearly"].includes(ent.plan_code) && ent.entitlement_status === "active" && !ent.lifetime;

  return (
    <div className="px-4 tablet:px-5 desktop:px-6 py-6 desktop:py-8 max-w-6xl mx-auto space-y-10" data-testid="plans-billing-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            <Sparkles className="h-3.5 w-3.5" /> C-ORE Membership
          </div>
          <h1 className="text-2xl md:text-3xl font-bold" style={{ color: NAVY }}>Plans &amp; Billing</h1>
          <p className="text-[13px] text-slate-500 mt-1">Manage your C-ORE subscription, payments and plan.</p>
        </div>
      </div>

      {err && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-[13px] text-rose-700" data-testid="billing-error">
          {err} <button onClick={load} className="underline ml-1">Retry</button>
        </div>
      )}

      {/* SECTION 1 — Current subscription */}
      <Section title="Current subscription" testid="section-current">
        {loading ? <Skeleton className="h-40 w-full" /> : (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="current-subscription-card">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <div className="rounded-xl p-2.5" style={{ background: `${NAVY}0d`, color: NAVY }}>
                    {React.createElement((PLAN_META[currentCode]?.icon) || ShieldCheck, { className: "h-6 w-6" })}
                  </div>
                  <div>
                    <div className="text-[20px] font-bold" style={{ color: NAVY }} data-testid="current-plan-name">
                      {planName(currentCode)}
                    </div>
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11.5px] font-semibold ${status.cls}`} data-testid="current-plan-status">
                      {status.label}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => scrollTo("section-plans")} data-testid="btn-upgrade-plan"
                  className="rounded-lg px-4 py-2 text-[13px] font-semibold text-white hover:opacity-90" style={{ background: NAVY }}>
                  {currentCode && currentCode !== "none" ? "Upgrade plan" : "Choose a plan"}
                </button>
                <button onClick={() => scrollTo("section-history")} data-testid="btn-manage-subscription"
                  className="rounded-lg border border-slate-200 px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50">
                  Manage
                </button>
                {isRecurringActive && (
                  <button onClick={() => setConfirmCancel(true)} data-testid="btn-cancel-top"
                    className="rounded-lg border border-rose-200 px-4 py-2 text-[13px] font-medium text-rose-700 hover:bg-rose-50">
                    Cancel
                  </button>
                )}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 desktop:grid-cols-4 gap-x-6 gap-y-4 text-[13px]">
              <Field label="Customer since" value={fmtDate(customer?.created_at)} testid="fld-customer-since" />
              <Field label="Trial remaining"
                value={ent?.plan_code === "trial" && ent?.days_remaining != null ? `${ent.days_remaining} days` : "—"} testid="fld-trial-remaining" />
              <Field label="Renewal date"
                value={ent?.lifetime ? "Never (lifetime)" : (ent?.cancel_at_period_end ? "Auto-renew off" : fmtDate(ent?.current_period_end))} testid="fld-renewal" />
              <Field label="Next billing"
                value={ent?.lifetime ? "—" : (isRecurringActive && !ent?.cancel_at_period_end
                  ? inr((plans.find((p) => p.code === ent.plan_code) || {}).amount_paise) : "—")} testid="fld-next-billing" />
            </div>

            {ent?.cancel_at_period_end && !ent?.lifetime && (
              <div className="mt-4 flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12.5px] text-amber-800">
                <Info className="h-4 w-4" /> Auto-renewal is off. You keep full access until {fmtDate(ent?.current_period_end)}.
              </div>
            )}
            {(!currentCode || currentCode === "none") && (
              <div className="mt-4 text-[12.5px] text-slate-500">
                You don&apos;t have an active plan yet. Start a free {(plans.find((p) => p.code === "trial") || {}).trial_days || 15}-day trial or pick a plan below.
              </div>
            )}
          </div>
        )}
      </Section>

      {/* SECTION 2 — Available plans */}
      <Section id="section-plans" testid="section-plans" title="Available plans"
        subtitle={cfg?.razorpay && !cfg.razorpay.key_configured ? "Payments are being configured — checkout is temporarily unavailable." : "Choose the plan that fits your business."}>
        {loading ? (
          <div className="grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-4 gap-4">{[0,1,2,3].map((i) => <Skeleton key={i} className="h-72" />)}</div>
        ) : (
          <div className="grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-4 gap-4">
            {plans.map((p) => {
              const meta = PLAN_META[p.code] || {};
              const isCurrent = p.code === currentCode;
              const isPaid = p.code !== "trial";
              const Icon = meta.icon || ShieldCheck;
              const badge = p.badge || meta.badge;
              const features = (p.features && p.features.length) ? p.features : (CARD_FEATURES[p.code] || []);
              const ctaLabel = CTA_LABELS[p.code] || p.button_text || (p.code === "trial" ? "Start free trial" : p.code === "lifetime" ? "Get Sovereign (1 Year)" : "Subscribe");
              const isFadedOut = !!selectedPlanCode && selectedPlanCode !== p.code;
              const isSelected = selectedPlanCode === p.code;
              return (
                <div key={p.code} data-testid={`plan-card-${p.code}`}
                  style={{
                    ...(badge ? { borderColor: meta.accent } : {}),
                    "--accent": meta.accent,
                    opacity: isFadedOut ? 0.4 : 1,
                    transform: isFadedOut ? "scale(0.98)" : isSelected ? "scale(1.02)" : undefined,
                    boxShadow: isSelected ? `0 25px 50px -12px ${meta.accent}66, 0 0 0 2px ${meta.accent}` : undefined,
                  }}
                  className={[
                    "relative rounded-2xl border bg-white p-5 flex flex-col shadow-sm",
                    "transition-all duration-200 ease-out transform-gpu",
                    badge ? "border-2" : "border-slate-200",
                    isPaid && !isFadedOut ? "cursor-pointer hover:-translate-y-1.5 hover:shadow-[0_25px_50px_-12px_var(--accent),0_0_0_1px_var(--accent)]" : "",
                    isFadedOut ? "pointer-events-none" : "",
                  ].join(" ")}>
                  {badge && (
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full px-2.5 py-0.5 text-[10.5px] font-bold text-white whitespace-nowrap"
                      style={{ background: meta.accent }}>{badge}</span>
                  )}
                  <div className="flex items-center gap-2">
                    <div className="rounded-lg p-2" style={{ background: `${meta.accent}14`, color: meta.accent }}><Icon className="h-5 w-5" /></div>
                    <div className="text-[14px] font-bold" style={{ color: NAVY }}>{p.name}</div>
                  </div>
                  <div className="mt-3">
                    <div className="text-[24px] font-extrabold leading-none" style={{ color: NAVY }}>
                      {p.code === "trial" ? "Free" : inr(p.amount_paise)}
                    </div>
                    <div className="text-[11.5px] text-slate-400 mt-1">
                      {p.code === "trial" ? `${p.trial_days || 0} days free` : meta.cadence}
                    </div>
                  </div>
                  <ul className="mt-4 space-y-1.5 flex-1">
                    {features.map((f) => (
                      <li key={f} className="flex items-start gap-1.5 text-[12px] text-slate-600">
                        <Check className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: meta.accent }} /> {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5">
                    {isCurrent ? (
                      <div className="w-full rounded-lg bg-slate-100 py-2.5 text-center text-[12.5px] font-semibold text-slate-500" data-testid={`plan-current-${p.code}`}>
                        Current plan
                      </div>
                    ) : (
                      <RazorpayCheckoutButton
                        mode={meta.mode}
                        planCode={p.code}
                        couponCode={coupon.trim() || null}
                        testid={`plan-cta-${p.code}`}
                        disabled={(cfg?.razorpay && !cfg.razorpay.key_configured) || isFadedOut}
                        className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg py-2.5 text-[13px] font-semibold text-white bg-[var(--accent)] hover:opacity-90 disabled:opacity-50 transition-all duration-200"
                        label={ctaLabel}
                        onResult={onCheckoutResult}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {!loading && (
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 pt-2" data-testid="trust-indicators">
            {TRUST_INDICATORS.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-1.5 text-[12px] text-slate-500">
                <Icon className="h-3.5 w-3.5 text-slate-400" /> {text}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* SECTION 4 — Coupon */}
      <Section title="Have a coupon?" subtitle="Discounts apply to the C-ORE Sovereign (1-year) plan." testid="section-coupon">
        <div className="rounded-xl border border-slate-200 bg-white p-5 max-w-xl">
          <div className="flex items-center gap-2">
            <input data-testid="coupon-input" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())}
              placeholder="Enter coupon code" className="flex-1 rounded-lg border border-slate-200 px-3 py-2.5 text-[13px] uppercase focus:border-slate-400 outline-none" />
            <button onClick={applyCoupon} disabled={couponBusy || !coupon.trim()} data-testid="coupon-apply"
              className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2.5 text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-50" style={{ background: NAVY }}>
              {couponBusy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Apply
            </button>
          </div>
          {couponMsg && (
            <div className={`mt-2.5 text-[12.5px] ${couponMsg.ok ? "text-emerald-700" : "text-rose-700"}`} data-testid="coupon-msg">{couponMsg.text}</div>
          )}
        </div>
      </Section>

      {/* SECTION 3 — Feature comparison */}
      <Section title="Compare plans" testid="section-compare">
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-[12.5px]" data-testid="comparison-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/60">
                <th className="text-left font-semibold text-slate-500 px-4 py-3">Feature</th>
                {["trial","monthly","yearly","lifetime"].map((c) => (
                  <th key={c} className="px-4 py-3 font-bold text-center" style={{ color: NAVY }}>{planName(c)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((row, i) => (
                <tr key={row.label} className={i % 2 ? "bg-slate-50/40" : ""}>
                  <td className="px-4 py-2.5 text-slate-700">{row.label}</td>
                  {["trial","monthly","yearly","lifetime"].map((c) => (
                    <td key={c} className="px-4 py-2.5 text-center">
                      {row[c] ? <Check className="h-4 w-4 text-emerald-600 inline" /> : <Minus className="h-4 w-4 text-slate-300 inline" />}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* SECTION 5 — Payment history */}
      <Section id="section-history" title="Payment history" testid="section-history">
        {loading ? <Skeleton className="h-28 w-full" /> : history.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white/60 p-8 text-center" data-testid="history-empty">
            <Receipt className="h-6 w-6 text-slate-300 mx-auto mb-2" />
            <div className="text-[13px] font-semibold text-slate-600">No payments yet</div>
            <div className="text-[12px] text-slate-400">Your invoices and receipts will appear here after your first payment.</div>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-[12.5px]" data-testid="history-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/60 text-slate-500">
                  {["Date","Plan","Amount","Status","Transaction","Method"].map((h) => (
                    <th key={h} className="text-left font-semibold px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-4 py-2.5 text-slate-700">{fmtDate(p.paid_at || p.created_at)}</td>
                    <td className="px-4 py-2.5 text-slate-700">{planName(p.plan_code)}</td>
                    <td className="px-4 py-2.5 font-semibold" style={{ color: NAVY }}>{inr(p.amount_paise)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${p.status === "captured" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : p.status === "failed" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-400 font-mono text-[11px]">{p.provider_payment_id || p.provider_order_id || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-500 capitalize">{p.payment_type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* SECTIONS 6/7/8 — supporting details */}
      <Section title="Billing details" testid="section-details">
        <div className="grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-2 gap-4">
          <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="billing-details-card">
            <div className="flex items-center gap-2 text-[13px] font-semibold" style={{ color: NAVY }}>
              <Building2 className="h-4 w-4" /> Company
            </div>
            <div className="mt-3 space-y-2 text-[13px]">
              <Field label="Company" value={customer?.company_name || customer?.name || "—"} testid="det-company" />
              <Field label="GSTIN" value={customer?.gstin || "Not on file"} testid="det-gstin" />
              <Field label="Billing email" value={customer?.email || "—"} testid="det-email" />
            </div>
            <p className="mt-3 text-[11.5px] text-slate-400">To update these, edit your business profile during setup or contact support.</p>
          </div>
          <div className="space-y-4">
            <PlaceholderCard icon={CreditCard} title="Payment method" note="Managed securely by Razorpay at checkout. Saved-card management is coming soon." testid="payment-method-placeholder" />
            <PlaceholderCard icon={Info} title="Usage" note="Usage metering (companies, syncs, AI requests, storage) isn't available yet." testid="usage-placeholder" />
          </div>
        </div>
      </Section>

      {/* SECTION 9 — Danger zone */}
      {isRecurringActive && (
        <Section title="Danger zone" testid="section-danger">
          <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-rose-500 mt-0.5" />
              <div>
                <div className="text-[13px] font-semibold text-rose-800">Cancel subscription</div>
                <div className="text-[12px] text-rose-600">Turns off auto-renewal. You keep access until the current paid period ends.</div>
              </div>
            </div>
            <button onClick={() => setConfirmCancel(true)} data-testid="btn-cancel-danger"
              className="rounded-lg border border-rose-300 bg-white px-4 py-2 text-[13px] font-semibold text-rose-700 hover:bg-rose-100">
              Cancel subscription
            </button>
          </div>
        </Section>
      )}

      {/* Cancel confirmation modal */}
      {confirmCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="cancel-modal" onClick={() => !cancelBusy && setConfirmCancel(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2 text-rose-700"><AlertTriangle className="h-5 w-5" /><span className="text-[15px] font-bold">Cancel subscription?</span></div>
              <button onClick={() => setConfirmCancel(false)} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
            </div>
            <p className="mt-3 text-[13px] text-slate-600">
              Auto-renewal will be turned off. You&apos;ll keep full C-ORE access until <b>{fmtDate(ent?.current_period_end)}</b>, after which the plan ends. You can resubscribe anytime.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setConfirmCancel(false)} disabled={cancelBusy} className="rounded-lg border border-slate-200 px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50">Keep plan</button>
              <button onClick={doCancel} disabled={cancelBusy} data-testid="cancel-confirm"
                className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-rose-700 disabled:opacity-50">
                {cancelBusy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Cancel subscription
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 rounded-lg bg-slate-900 px-4 py-2.5 text-[13px] text-white shadow-lg" data-testid="billing-toast">{toast}</div>
      )}
    </div>
  );
}

const Field = ({ label, value, testid }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className="text-[13px] font-semibold text-slate-800 mt-0.5" data-testid={testid}>{value}</div>
  </div>
);
